import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import FloatingBackButton from './FloatingBackButton';
import { TERMS_CONTENT } from '../data/termsContent';

const Terms: React.FC = () => {
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const c = TERMS_CONTENT[i18n.language === 'en' ? 'en' : 'fr'];

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <FloatingBackButton
        onBack={() => navigate(-1)}
        mode="inline"
        variant="with-text"
        text={c.backLabel}
        className="mb-6"
      />

      <h1 className="text-3xl font-bold mb-8 text-gray-900">
        {c.pageTitle}
      </h1>

      <div className="prose prose-lg max-w-none">
        <p className="text-gray-600 mb-6">
          <strong>{c.lastUpdatedLabel}</strong> {c.lastUpdatedDate}
        </p>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-4 text-gray-800">
            {c.section1Title}
          </h2>
          <p className="mb-4 text-gray-700 leading-relaxed">
            {c.section1Body}
          </p>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-4 text-gray-800">
            {c.section2Title}
          </h2>
          <p className="mb-4 text-gray-700 leading-relaxed">
            {c.section2Intro}
          </p>
          <ul className="list-disc pl-6 mb-4 text-gray-700">
            {c.section2Items.map((item, i) => (
              <li key={i}>{item}</li>
            ))}
          </ul>
          <p className="mb-4 text-gray-700 leading-relaxed">
            {c.section2Body2}
          </p>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-4 text-gray-800">
            {c.section3Title}
          </h2>
          <p className="mb-4 text-gray-700 leading-relaxed">
            {c.section3Intro}
          </p>
          <ul className="list-disc pl-6 mb-4 text-gray-700">
            {c.section3Items.map((item, i) => (
              <li key={i}>{item}</li>
            ))}
          </ul>
          <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mb-4">
            <p className="text-gray-700">
              <strong>{c.section3NoticeLabel}</strong> {c.section3NoticeBody}
            </p>
          </div>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-4 text-gray-800">
            {c.section4Title}
          </h2>

          <h3 className="text-xl font-semibold mb-3 text-gray-700">{c.section4_1Title}</h3>
          <p className="mb-4 text-gray-700 leading-relaxed">
            {c.section4_1Body}
          </p>

          <h3 className="text-xl font-semibold mb-3 text-gray-700">{c.section4_2Title}</h3>
          <p className="mb-4 text-gray-700 leading-relaxed">
            {c.section4_2Body}
          </p>

          <h3 className="text-xl font-semibold mb-3 text-gray-700">{c.section4_3Title}</h3>
          <p className="mb-4 text-gray-700 leading-relaxed">
            {c.section4_3Body}
          </p>
          <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-4">
            <p className="text-gray-700">
              <strong>{c.section4_3NoticeLabel}</strong> {c.section4_3NoticeBody}
            </p>
          </div>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-4 text-gray-800">
            {c.section5Title}
          </h2>
          <p className="mb-4 text-gray-700 leading-relaxed">
            {c.section5Intro}
          </p>
          <ul className="list-disc pl-6 mb-4 text-gray-700">
            {c.section5Items.map((item, i) => (
              <li key={i}>{item}</li>
            ))}
          </ul>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-4 text-gray-800">
            {c.section6Title}
          </h2>
          <p className="mb-4 text-gray-700 leading-relaxed">
            {c.section6Body}
          </p>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-4 text-gray-800">
            {c.section7Title}
          </h2>
          <p className="mb-4 text-gray-700 leading-relaxed">
            {c.section7BodyBefore}{' '}
            <Link to="/privacy" className="text-blue-600 hover:underline">{c.privacyLinkText}</Link>
            {c.section7BodyAfter}
          </p>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-4 text-gray-800">
            {c.section8Title}
          </h2>
          <p className="mb-4 text-gray-700 leading-relaxed">
            {c.section8Body}
          </p>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-4 text-gray-800">
            {c.section9Title}
          </h2>
          <p className="mb-4 text-gray-700 leading-relaxed">
            {c.section9Body}
          </p>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-4 text-gray-800">
            {c.section10Title}
          </h2>
          <p className="mb-4 text-gray-700 leading-relaxed">
            {c.section10Body}
          </p>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-4 text-gray-800">
            {c.section11Title}
          </h2>
          <p className="mb-4 text-gray-700 leading-relaxed">
            {c.section11Body}
          </p>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-4 text-gray-800">
            {c.section12Title}
          </h2>
          <p className="mb-4 text-gray-700 leading-relaxed">
            {c.section12Body}
          </p>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-4 text-gray-800">
            {c.section13Title}
          </h2>
          <div className="bg-gray-50 p-4 rounded-lg">
            <p className="text-gray-700">
              <strong>{c.contactEmailLabel}</strong>{' '}
              <a href="mailto:jogolinga@gmail.com" className="text-blue-600 hover:underline">{c.contactEmail}</a><br />
              <strong>{c.contactAppLabel}</strong> {c.contactAppName}
            </p>
          </div>
        </section>
      </div>
    </div>
  );
};

export default Terms;
